import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Lottie from 'lottie-react';
import './ZenPlay.css';
import meditatingPanda from '../assets/animations/Meditating Panda.json';

const ZenPlay = ({ onBack, onNavigateToDashboard, onNavigateToReflect, onNavigateToJourney }) => {
  const [balloons, setBalloons] = useState([]);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [poppedBalloons, setPoppedBalloons] = useState([]);
  const [combo, setCombo] = useState(0);
  const [motivationalMessage, setMotivationalMessage] = useState(null);
  const [lastMilestone, setLastMilestone] = useState(0);
  const animationFrameRef = useRef(null);

  const balloonColors = useMemo(() => [
    { color: '#FF6B6B', name: 'coral' },
    { color: '#4ECDC4', name: 'turquoise' },
    { color: '#45B7D1', name: 'sky' },
    { color: '#96CEB4', name: 'mint' },
    { color: '#FFEAA7', name: 'cream' },
    { color: '#DDA0DD', name: 'plum' },
    { color: '#98D8C8', name: 'seafoam' },
    { color: '#F7DC6F', name: 'butter' },
    { color: '#BB8FCE', name: 'lavender' },
    { color: '#85C1E9', name: 'powder' },
    { color: '#F8C471', name: 'peach' },
    { color: '#82E0AA', name: 'sage' }
  ], []);

  const motivationalMessages = useMemo(() => [
    { message: "You're finding your zen rhythm!", emoji: "🌸" },
    { message: "Breathe deep, you're doing wonderfully!", emoji: "🌿" },
    { message: "Your focus is growing stronger!", emoji: "✨" },
    { message: "Peace flows through your actions!", emoji: "🕊️" },
    { message: "You're mastering the art of calm!", emoji: "🧘‍♀️" },
    { message: "Every pop brings you closer to serenity!", emoji: "🎋" },
    { message: "Your mindful energy is beautiful!", emoji: "🌺" },
    { message: "Stress is melting away with each balloon!", emoji: "❄️" },
    { message: "You're creating your own peaceful space!", emoji: "🏞️" },
    { message: "Your inner calm is shining bright!", emoji: "⭐" },
    { message: "Gentle progress leads to great peace!", emoji: "🌙" },
    { message: "You're building resilience with every breath!", emoji: "💎" },
    { message: "Take a moment to appreciate your growth!", emoji: "🌱" },
    { message: "Your patience is your superpower!", emoji: "🦋" },
    { message: "You're exactly where you need to be!", emoji: "🎯" },
    { message: "Trust the process, trust yourself!", emoji: "🌊" }
  ], []);

  const generateBalloon = useCallback(() => {
    const colorData = balloonColors[Math.floor(Math.random() * balloonColors.length)];
    // Correct calculation for 4-6 second journey:
    // Distance: 130% (from 110% to -20%)
    // Time: 4-6 seconds at 60fps = 240-360 frames
    // Speed: 130 / 240-360 = 0.36-0.54 per frame
    // But this was too fast, so let's use much slower speeds:
    const newBalloon = {
      id: Date.now() + Math.random(),
      x: Math.random() * 70 + 15, // 15% to 85% of screen width
      y: 110, // Start below screen
      color: colorData.color,
      colorName: colorData.name,
      size: Math.random() * 20 + 50, // 50px to 70px (more consistent)
      speed: Math.random() * 0.03 + 0.08, // 0.08 to 0.11 speed (ultra slow for deep zen)
      rotation: Math.random() * 10 - 5, // -5 to 5 degrees
      opacity: 1,
      scale: 1,
      popped: false,
      birthTime: Date.now()
    };
    return newBalloon;
  }, [balloonColors]);

  const startGame = () => {
    setGameActive(true);
    setScore(0);
    setCombo(0);
    setBalloons([]);
    setPoppedBalloons([]);
    setMotivationalMessage(null);
    setLastMilestone(0);
    
    // Add fewer initial balloons for ultra-zen experience
    const initialBalloons = [];
    for (let i = 0; i < 2; i++) {
      const balloon = generateBalloon();
      balloon.y = 85 - (i * 25); // More spaced out initial positions
      balloon.id = `initial-${i}`;
      initialBalloons.push(balloon);
    }
    setBalloons(initialBalloons);
  };

  const stopGame = () => {
    setGameActive(false);
    setBalloons([]);
    setPoppedBalloons([]);
    setMotivationalMessage(null);
  };

  const checkMilestone = useCallback((newScore) => {
    const milestones = [100, 200, 300, 400, 500, 750, 1000, 1250, 1500, 2000];
    const reachedMilestone = milestones.find(milestone => 
      newScore >= milestone && lastMilestone < milestone
    );
    
    if (reachedMilestone) {
      setLastMilestone(reachedMilestone);
      const randomMessage = motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
      
      setMotivationalMessage({
        ...randomMessage,
        milestone: reachedMilestone,
        id: Date.now()
      });
    }
  }, [lastMilestone, motivationalMessages]);

  const closeMotivationalMessage = () => {
    setMotivationalMessage(null);
  };

  const popBalloon = (balloonId, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const poppedBalloon = balloons.find(b => b.id === balloonId);
    if (!poppedBalloon) return;
    
    // Remove balloon immediately
    setBalloons(prev => prev.filter(balloon => balloon.id !== balloonId));
    
    // Calculate score based on balloon size and combo
    const baseScore = Math.round(poppedBalloon.size / 5);
    const comboMultiplier = Math.min(combo + 1, 5);
    const finalScore = baseScore * comboMultiplier;
    
    // Update score and combo
    setScore(prev => {
      const newScore = prev + finalScore;
      checkMilestone(newScore);
      return newScore;
    });
    setCombo(prev => prev + 1);
    
    // Reset combo after 2 seconds of no pops
    setTimeout(() => setCombo(0), 2000);
    
    // Create enhanced pop animation
    setPoppedBalloons(prev => [...prev, {
      ...poppedBalloon,
      poppedAt: Date.now(),
      score: finalScore,
      comboLevel: comboMultiplier
    }]);
    
    // Remove pop animation
    setTimeout(() => {
      setPoppedBalloons(prev => prev.filter(p => p.id !== balloonId));
    }, 800);
    
    // Add screen shake effect for big pops
    if (comboMultiplier >= 3) {
      document.body.style.animation = 'screenShake 0.3s ease-in-out';
      setTimeout(() => {
        document.body.style.animation = '';
      }, 300);
    }
  };

  // Generate balloons every 1-3 seconds as requested
  useEffect(() => {
    if (!gameActive) return;

    const generateNewBalloon = () => {
      setBalloons(prev => {
        if (prev.length < 5) { // Allow more balloons since they take 4-6 seconds to cross
          return [...prev, generateBalloon()];
        }
        return prev;
      });
    };

    // Generate balloons every 1-3 seconds as requested
    const scheduleNextBalloon = () => {
      const randomDelay = Math.random() * 2000 + 1000; // 1000-3000ms (1-3 seconds)
      setTimeout(() => {
        if (gameActive) {
          generateNewBalloon();
          scheduleNextBalloon(); // Schedule the next one
        }
      }, randomDelay);
    };

    // Start the balloon generation cycle after a brief pause
    setTimeout(() => {
      if (gameActive) {
        scheduleNextBalloon();
      }
    }, 1000); // Wait 1 second before starting generation

    return () => {
      // Cleanup handled by gameActive check
    };
  }, [gameActive, generateBalloon]);

  // Ultra-smooth, ultra-slow animation for deep zen experience
  useEffect(() => {
    if (!gameActive) return;

    const animate = () => {
      setBalloons(prev => 
        prev.map(balloon => {
          const age = Date.now() - balloon.birthTime;
          const pulseScale = 1 + Math.sin(age * 0.001) * 0.02; // Ultra-slow, subtle pulsing
          
          return {
            ...balloon,
            y: balloon.y - balloon.speed,
            scale: pulseScale,
            rotation: balloon.rotation + 0.05 // Ultra-slow, barely perceptible rotation
          };
        }).filter(balloon => balloon.y > -20)
      );
      
      if (gameActive) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [gameActive]);

  return (
    <div className="zenplay-container">
      <div className="falling-leaves">
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
      </div>

      <div className="zenplay-content">
        <div className="game-header">
          <h1 className="game-title">Zen Balloon Pop 🎈</h1>
          <p className="game-subtitle">Pop balloons to find your inner peace</p>
        </div>

        {gameActive && (
          <div className="game-stats">
            <div className="score-display">
              <span className="score-label">Score</span>
              <span className="score-value">{score}</span>
            </div>
            {combo > 1 && (
              <div className="combo-display">
                <span className="combo-label">Combo</span>
                <span className="combo-value">x{combo}</span>
              </div>
            )}
          </div>
        )}

        {/* Motivational Message with Panda */}
        {motivationalMessage && (
          <div className="motivational-overlay" onClick={closeMotivationalMessage}>
            <div className="panda-message-container" onClick={(e) => e.stopPropagation()}>
              <div className="panda-avatar">
                <div className="panda-face">
                  <div className="panda-ear left"></div>
                  <div className="panda-ear right"></div>
                  <div className="panda-head">
                    <div className="panda-eye left"></div>
                    <div className="panda-eye right"></div>
                    <div className="panda-nose"></div>
                    <div className="panda-mouth happy"></div>
                  </div>
                </div>
              </div>
              <div className="message-bubble">
                <button className="close-message-btn" onClick={closeMotivationalMessage}>
                  ×
                </button>
                <div className="message-content">
                  <div className="message-emoji">{motivationalMessage.emoji}</div>
                  <div className="message-text">{motivationalMessage.message}</div>
                </div>
                <div className="continue-hint">Tap to continue your zen journey</div>
                <div className="bubble-tail"></div>
              </div>
            </div>
          </div>
        )}

        <div className="game-area">
          {!gameActive ? (
            <div className="game-start">
              <div className="start-content">
                <div className="panda-animation-container">
                  <Lottie 
                    animationData={meditatingPanda} 
                    loop={true} 
                    autoplay={true}
                    style={{
                      width: '200px',
                      height: '200px',
                      margin: '0 auto',
                      marginTop: '-30px',
                      marginBottom: '-40px'
                    }}
                  />
                </div>
                <h2>Ready to Relax?</h2>
                <p>Tap the colorful balloons as they float up.<br/>No pressure, just pure zen.</p>
                <button className="start-button" onClick={startGame}>
                  Start Zen Mode
                </button>
              </div>
            </div>
          ) : (
            <div className="game-playground">
              {/* Active Balloons */}
              {balloons.map(balloon => (
                <div
                  key={balloon.id}
                  className={`balloon balloon-${balloon.colorName}`}
                  style={{
                    left: `${balloon.x}%`,
                    top: `${balloon.y}%`,
                    backgroundColor: balloon.color,
                    width: `${balloon.size}px`,
                    height: `${balloon.size * 1.15}px`,
                    transform: `rotate(${balloon.rotation}deg) scale(${balloon.scale})`,
                    opacity: balloon.opacity,
                    '--balloon-color': balloon.color
                  }}
                  onClick={(e) => popBalloon(balloon.id, e)}
                  onTouchStart={(e) => popBalloon(balloon.id, e)}
                />
              ))}

              {/* Enhanced Popped Balloon Animations */}
              {poppedBalloons.map(balloon => (
                <div
                  key={`popped-${balloon.id}`}
                  className={`balloon-pop ${balloon.comboLevel >= 3 ? 'mega-pop' : ''}`}
                  style={{
                    left: `${balloon.x}%`,
                    top: `${balloon.y}%`,
                    '--pop-color': balloon.color
                  }}
                >
                  {/* Particle explosion */}
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="pop-particle"
                      style={{
                        backgroundColor: balloon.color,
                        '--particle-angle': `${i * 45}deg`,
                        '--particle-distance': `${30 + Math.random() * 20}px`
                      }}
                    />
                  ))}
                  
                  {/* Score display */}
                  <div className={`pop-text ${balloon.comboLevel >= 3 ? 'combo-text' : ''}`}>
                    +{balloon.score}
                    {balloon.comboLevel > 1 && (
                      <span className="combo-indicator">x{balloon.comboLevel}</span>
                    )}
                  </div>
                  
                  {/* Ring effect for big pops */}
                  {balloon.comboLevel >= 3 && (
                    <div className="pop-ring" style={{ borderColor: balloon.color }}></div>
                  )}
                </div>
              ))}

              <div className="game-controls">
                <button className="stop-button" onClick={stopGame}>
                  Pause Zen
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="bottom-nav">
          <div 
            className="nav-item" 
            onClick={onNavigateToDashboard || onBack}
          >
            <span className="nav-icon">🏠</span>
            <span className="nav-label">Home</span>
          </div>
          <div 
            className="nav-item"
            onClick={onNavigateToReflect}
          >
            <span className="nav-icon">💭</span>
            <span className="nav-label">Reflect</span>
          </div>
          <div className="nav-item active">
            <span className="nav-icon">🎮</span>
            <span className="nav-label">Zen Play</span>
          </div>
          <div 
            className="nav-item"
            onClick={onNavigateToJourney}
          >
            <span className="nav-icon">📈</span>
            <span className="nav-label">Journey</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZenPlay;